'use client'
import { useState, useMemo } from 'react'
import {
  CartesianGrid,
  Line,
  LineChart,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
} from 'recharts'
import { PoundCircleIcon } from '@/assets/svg'
import { cn } from '@/lib/utils'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Skeleton } from '@/components/ui/skeleton'

const formatYAxis = (value: number) => {
  if (value >= 1000000) return `£${value / 1000000}M`
  if (value >= 1000) return `£${value / 1000}k`
  return `£${value}`
}

const paymentFilterOptions = [
  { value: 'all', label: 'All Payments' },
  { value: 'successful', label: 'Successful' },
  { value: 'pending', label: 'Pending' },
  { value: 'failed', label: 'Failed' },
]

interface PaymentTrendsData {
  day: string
  date: string
  successful: { amount_cents: number; amount: number }
  pending: { amount_cents: number; amount: number }
  failed: { amount_cents: number; amount: number }
}

interface PaymentTrends {
  period: 'weekly' | 'monthly'
  data: PaymentTrendsData[]
}

interface PaymentTrendsChartProps {
  className?: string
  paymentTrends?: PaymentTrends
  loading?: boolean
  trendPeriod?: 'weekly' | 'monthly'
  onTrendPeriodChange?: (period: 'weekly' | 'monthly') => void
}

export default function PaymentTrendsChart({
  className,
  paymentTrends,
  loading = false,
  trendPeriod,
  onTrendPeriodChange,
}: PaymentTrendsChartProps) {
  const [paymentFilter, setPaymentFilter] = useState('all')

  // Use trendPeriod from props or fallback to data period or 'weekly'
  const currentPeriod = trendPeriod || paymentTrends?.period || 'weekly'

  const handlePeriodChange = (period: 'weekly' | 'monthly') => {
    if (onTrendPeriodChange) {
      onTrendPeriodChange(period)
    }
  }

  // Transform payment trends data for chart
  const chartData = useMemo(() => {
    if (!paymentTrends?.data) return []

    return paymentTrends.data.map((item) => ({
      day: item.day,
      date: item.date,
      successful: item.successful?.amount || 0,
      pending: item.pending?.amount || 0,
      failed: item.failed?.amount || 0,
    }))
  }, [paymentTrends])

  // Format X-axis labels based on period
  const formatXAxisLabel = (value: string) => {
    if (currentPeriod === 'monthly') {
      return `Day ${value}`
    }
    return value
  }

  // Filter chart data based on paymentFilter
  const filteredChartData = useMemo(() => {
    if (paymentFilter === 'all') return chartData

    return chartData.map((item) => ({
      ...item,
      ...(paymentFilter === 'successful' && { pending: 0, failed: 0 }),
      ...(paymentFilter === 'pending' && { successful: 0, failed: 0 }),
      ...(paymentFilter === 'failed' && { successful: 0, pending: 0 }),
    }))
  }, [chartData, paymentFilter])

  // Calculate max value for Y-axis ticks
  const maxValue = useMemo(() => {
    if (filteredChartData.length === 0) return 200000
    const allValues = filteredChartData.flatMap((item) => [
      item.successful,
      item.pending,
      item.failed,
    ])
    const max = Math.max(...allValues)
    // Round up to nearest nice number
    if (max === 0) return 200000
    const magnitude = Math.pow(10, Math.floor(Math.log10(max)))
    return Math.ceil(max / magnitude) * magnitude
  }, [filteredChartData])

  // Generate Y-axis ticks
  const yAxisTicks = useMemo(() => {
    const ticks = [0]
    const step = maxValue / 5
    for (let i = step; i <= maxValue; i += step) {
      ticks.push(Math.round(i))
    }
    return ticks
  }, [maxValue])

  // Show loading skeleton when loading and either:
  // 1. No data exists (initial load), or
  // 2. Period mismatch (switching periods)
  const showLoadingSkeleton =
    loading && (!paymentTrends?.data || paymentTrends?.period !== currentPeriod)

  return (
    <div className={cn('bg-white rounded-[1.15rem] p-5', className)}>
      <div className='flex justify-between items-center mb-6'>
        <div className='flex items-center gap-3'>
          <span className='bg-gray-50 p-2 rounded-full'>
            <figure>{PoundCircleIcon}</figure>
          </span>
          <h3 className='text-[1.1rem] SFPro-Semibold font-bold tracking-wider'>
            Payment Trends
          </h3>
        </div>
        <div className='flex items-center gap-3'>
          <Select
            value={paymentFilter}
            onValueChange={setPaymentFilter}
            disabled={loading}
          >
            <SelectTrigger size='sm' className='w-[150px]'>
              <SelectValue placeholder='Select payment type' />
            </SelectTrigger>
            <SelectContent>
              {paymentFilterOptions.map((option) => (
                <SelectItem key={option.value} value={option.value}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <div className='flex bg-gray-100 rounded-lg p-1'>
            <button
              onClick={() => handlePeriodChange('weekly')}
              disabled={loading}
              className={cn(
                'px-4 py-1.5 text-sm rounded-md transition-colors cursor-pointer',
                currentPeriod === 'weekly'
                  ? 'bg-[#295532] text-white'
                  : 'text-gray-600 hover:text-gray-800',
                loading && 'opacity-50 cursor-not-allowed'
              )}
            >
              Weekly
            </button>
            <button
              onClick={() => handlePeriodChange('monthly')}
              disabled={loading}
              className={cn(
                'px-4 py-1.5 text-sm rounded-md transition-colors cursor-pointer',
                currentPeriod === 'monthly'
                  ? 'bg-[#295532] text-white'
                  : 'text-gray-600 hover:text-gray-800',
                loading && 'opacity-50 cursor-not-allowed'
              )}
            >
              Monthly
            </button>
          </div>
        </div>
      </div>

      {showLoadingSkeleton ? (
        <Skeleton className='h-[300px] w-full' />
      ) : (
        <>
          <div className='flex gap-6 mb-4'>
            <div className='flex items-center gap-2'>
              <span className='w-3 h-3 rounded-full bg-[#295532]' />
              <span className='text-sm text-gray-600'>Successful</span>
            </div>
            <div className='flex items-center gap-2'>
              <span className='w-3 h-3 rounded-full bg-[#F59E0B]' />
              <span className='text-sm text-gray-600'>Pending</span>
            </div>
            <div className='flex items-center gap-2'>
              <span className='w-3 h-3 rounded-full bg-[#EF4444]' />
              <span className='text-sm text-gray-600'>Failed</span>
            </div>
          </div>

          <div className='h-[300px] w-full'>
            <ResponsiveContainer width='100%' height='100%'>
              <LineChart
                data={filteredChartData}
                margin={{ top: 10, right: 10, left: 0, bottom: 0 }}
              >
                <CartesianGrid
                  strokeDasharray='3 3'
                  vertical={false}
                  stroke='#E5E7EB'
                />
                <XAxis
                  dataKey='day'
                  tickLine={false}
                  axisLine={false}
                  tickMargin={12}
                  tick={{ fill: '#9CA3AF', fontSize: 12 }}
                  tickFormatter={formatXAxisLabel}
                />
                <YAxis
                  tickLine={false}
                  axisLine={{ stroke: '#E5E7EB' }}
                  tickMargin={8}
                  tick={{ fill: '#9CA3AF', fontSize: 12 }}
                  tickFormatter={formatYAxis}
                  ticks={yAxisTicks}
                />
                <Tooltip
                  contentStyle={{
                    backgroundColor: '#fff',
                    border: '1px solid #E5E7EB',
                    borderRadius: '8px',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                  }}
                  formatter={(value: number) => [
                    `£${value.toLocaleString(undefined, {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    })}`,
                    '',
                  ]}
                  labelFormatter={(label: any, payload: readonly any[]) => {
                    if (!payload || payload.length === 0) return String(label)
                    const dataPoint = payload[0]?.payload
                    // For monthly, show date; for weekly, show day name
                    if (currentPeriod === 'monthly' && dataPoint?.date) {
                      try {
                        const date = new Date(dataPoint.date)
                        if (!isNaN(date.getTime())) {
                          return date.toLocaleDateString('en-US', {
                            month: 'short',
                            day: 'numeric',
                          })
                        }
                      } catch (e) {
                        // Fallback to label if date parsing fails
                      }
                    }
                    return String(label)
                  }}
                  labelStyle={{ fontWeight: 600, marginBottom: 4 }}
                />
                {(paymentFilter === 'all' ||
                  paymentFilter === 'successful') && (
                  <Line
                    type='monotone'
                    dataKey='successful'
                    stroke='#295532'
                    strokeWidth={2.5}
                    dot={false}
                    name='Successful'
                  />
                )}
                {(paymentFilter === 'all' || paymentFilter === 'pending') && (
                  <Line
                    type='monotone'
                    dataKey='pending'
                    stroke='#F59E0B'
                    strokeWidth={2.5}
                    dot={false}
                    name='Pending'
                  />
                )}
                {(paymentFilter === 'all' || paymentFilter === 'failed') && (
                  <Line
                    type='monotone'
                    dataKey='failed'
                    stroke='#EF4444'
                    strokeWidth={2.5}
                    dot={false}
                    name='Failed'
                  />
                )}
              </LineChart>
            </ResponsiveContainer>
          </div>
        </>
      )}
    </div>
  )
}
